import { useEffect, useRef } from "react";
import "./index.css";
import {
  widget,
  ChartingLibraryWidgetOptions,
  LanguageCode,
  ResolutionString,
  IChartingLibraryWidget,
} from "../../charting_library";
import * as React from "react";
import Datafeed from "./api/index";

export interface ChartContainerProps {
  symbol: ChartingLibraryWidgetOptions["symbol"];
  interval: ChartingLibraryWidgetOptions["interval"];

  // BEWARE: no trailing slash is expected in feed URL
  datafeedUrl: string;
  libraryPath: ChartingLibraryWidgetOptions["library_path"];
  chartsStorageUrl: ChartingLibraryWidgetOptions["charts_storage_url"];
  chartsStorageApiVersion: ChartingLibraryWidgetOptions["charts_storage_api_version"];
  clientId: ChartingLibraryWidgetOptions["client_id"];
  userId: ChartingLibraryWidgetOptions["user_id"];
  fullscreen: ChartingLibraryWidgetOptions["fullscreen"];
  autosize: ChartingLibraryWidgetOptions["autosize"];
  studiesOverrides: ChartingLibraryWidgetOptions["studies_overrides"];
  container: ChartingLibraryWidgetOptions["container"];
  theme: ChartingLibraryWidgetOptions["theme"];
  custom_css_url: ChartingLibraryWidgetOptions["custom_css_url"];
  disabled_features: ChartingLibraryWidgetOptions["disabled_features"];
  enabled_features: ChartingLibraryWidgetOptions["enabled_features"];
  overrides: ChartingLibraryWidgetOptions["overrides"];
  //toolbar_bg: ChartingLibraryWidgetOptions["toolbar_bg"];
}

/* const INTERVAL = {
  MINUTE: "1",
  MINUTES_5: "5",
  MINUTES_15: "15",
  MINUTES_30: "30",
  HOUR: "60",
  HOURS_3: "180",
  HOURS_6: "360",
  HOURS_12: "720",
  DAY: "D",
  WEEK: "W",
};

const TIME_FRAMES = [
  { text: "3y", resolution: INTERVAL.WEEK, description: "3 Years" },
  { text: "1y", resolution: INTERVAL.DAY, description: "1 Year" },
  { text: "3m", resolution: INTERVAL.HOURS_12, description: "3 Months" },
  { text: "1m", resolution: INTERVAL.HOURS_6, description: "1 Month" },
  { text: "7d", resolution: INTERVAL.HOUR, description: "7 Days" },
  { text: "3d", resolution: INTERVAL.MINUTES_30, description: "3 Days" },
  { text: "1d", resolution: INTERVAL.MINUTES_15, description: "1 Day" },
  { text: "6h", resolution: INTERVAL.MINUTES_5, description: "6 Hours" },
  { text: "1h", resolution: INTERVAL.MINUTE, description: "1 Hour" },
]; */

const getLanguageFromURL = (): LanguageCode | null => {
  const regex = new RegExp("[\\?&]lang=([^&#]*)");
  const results = regex.exec(location.search);
  return results === null
    ? null
    : (decodeURIComponent(results[1].replace(/\+/g, " ")) as LanguageCode);
};

export type TVChartContainerProps = {
  symbol: string;
  isMobile: boolean;
  interval?: string;
  fullscreen: boolean;
};

export let tvWidget: IChartingLibraryWidget;

export const TVChartContainer = (props: TVChartContainerProps) => {
  const { fullscreen, isMobile, symbol } = props;
  const [lineValue, setLineValue] = React.useState<number>();

  const chartContainerRef =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;

  const defaultProps: Omit<ChartContainerProps, "container"> = {
    symbol: symbol,
    // custom_css_url: "/custom.css",
    interval: "1" as ResolutionString,
    datafeedUrl: "https://demo_feed.tradingview.com",
    libraryPath: "charting_library/",
    custom_css_url: "../custom.css",
    chartsStorageUrl: "https://saveload.tradingview.com",
    chartsStorageApiVersion: "1.1",
    clientId: "tradingview.com",
    userId: "public_user_id",
    fullscreen: fullscreen,
    autosize: true,
    theme: "light",
    studiesOverrides: {},
    disabled_features: ["left_toolbar"],
    enabled_features: ["hide_left_toolbar_by_default"],
    overrides: {},
    //toolbar_bg: "#000000",
  };

  /* const handlePositionLineDraw = () => {
    if (lineValue) {
      console.log("drawing line at - ", lineValue);
      tvWidget.chart().createPositionLine().setPrice(lineValue);
    }
  }; */

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      console.log("Message received! ", event.data);
      if (event.data) {
        setLineValue(Number(event.data));
      }
    };

    window.addEventListener("message", handler);

    // clean up
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    const widgetOptions: ChartingLibraryWidgetOptions = {
      debug: false,
      symbol: defaultProps.symbol as string,
      // tslint:disable-next-line:no-any
      loading_screen: {
        foregroundColor: "#0E2965",
        backgroundColor: "#0E2965",
      },
      datafeed: Datafeed,
      interval:
        defaultProps.interval as ChartingLibraryWidgetOptions["interval"],
      container: chartContainerRef.current,
      library_path: defaultProps.libraryPath as string,
      timezone: "America/Toronto",
      locale: getLanguageFromURL() || "en",
      disabled_features: [
        "use_localstorage_for_settings",
        //"legend_widget",
        "header_compare",
        "header_undo_redo",
        //"left_toolbar",
        "header_saveload",
        "header_settings",
        "disable_resolution_rebuild",
        //'edit_buttons_in_legend',
        "legend_context_menu",
        //'study_templates',
        "timeframes_toolbar", //bottom bar
        "control_bar",
        "header_fullscreen_button",
        "popup_hints",
        //"header_symbol_search",
      ],
      enabled_features: [
        //"hide_left_toolbar_by_default",
        "hide_last_na_study_output",
        "study_symbol_ticker_description",
        "edit_buttons_in_legend",
        "mouse_wheel_scale",
        "volume_force_overlay",
      ],
      charts_storage_url: defaultProps.chartsStorageUrl,
      charts_storage_api_version: defaultProps.chartsStorageApiVersion,
      client_id: defaultProps.clientId,
      user_id: defaultProps.userId,
      theme: defaultProps.theme,
      fullscreen: true,
      autosize: defaultProps.autosize,
      //toolbar_bg: defaultProps.toolbar_bg,
      overrides: {
        "mainSeriesProperties.style": 1,
        "mainSeriesProperties.showCountdown": false,
        "paneProperties.background": "#0E2965",
        // "paneProperties.vertGridProperties.color": !isMobile
        //   ? "#292962"
        //   : "#0E2965",
        // "paneProperties.horzGridProperties.color": !isMobile
        //   ? "#292962"
        //   : "#0E2965",
        "paneProperties.legendProperties.showSeriesTitle": true,
        "paneProperties.legendProperties.showSeriesOHLC": !isMobile,
        "paneProperties.legendProperties.showLegend": false,
        "scalesProperties.textColor": "#FFFFFF",
        "scalesProperties.backgroundColor": "#0E2965",
        "scalesProperties.lineColor": !isMobile ? "#292962" : "#0E2965",
        "paneProperties.topMargin": 10,
        "paneProperties.bottomMargin": 8,
        "mainSeriesProperties.priceLineColor": "cyan",
        "mainSeriesProperties.candleStyle.drawBorder": false,
        "mainSeriesProperties.candleStyle.borderColor": "#ffffff",
        "mainSeriesProperties.candleStyle.upColor": "#3cf52f",
        "mainSeriesProperties.candleStyle.downColor": "#c41408",
      },
      custom_css_url: defaultProps.custom_css_url,
    };

    tvWidget = new widget(widgetOptions);

    tvWidget.onChartReady(() => {
      tvWidget.headerReady().then(() => {
        tvWidget.chart().createStudy("Moving Average", false, true, undefined, {
          showLabelsOnPriceScale: false,
        });
        tvWidget.chart().createStudy("Volume", true, true);
        tvWidget.applyStudiesOverrides({
          "moving average.length": 24,
          "volume.volume.color.0": "rgba(196, 20, 8, 0.3)",
          "volume.volume.color.1": "rgba(60, 245, 47, 0.3)",
        });
        if (lineValue) {
          tvWidget.chart().createPositionLine().setPrice(lineValue);
        }
      });
    });

    return () => {
      tvWidget.remove();
    };
  });

  /* useEffect(() => {
    if (lineValue) {
      console.log("drawing");
      //fix this
      //tvWidget.activeChart().createPositionLine().setPrice(lineValue);
    }
  }, [lineValue]); */

  return <div ref={chartContainerRef} className={"TVChartContainer"} />;
};
